// index.tsx
// CRITICAL: This MUST be the first import to fix Electron file:// protocol
import './set-public-path';

// Install the mixpanel-browser IndexedDB rejection guard before any other
// code runs. mixpanel-browser's session replay module throws NotFoundError
// from inside uncaught promise chains when the persisted IDB schema is stale;
// installing the listener synchronously here ensures it is live before the
// SDK bundle loads and starts firing rejections.
import { installMixpanelRejectionGuard } from './services/analytics/mixpanelIdbGuard';
installMixpanelRejectionGuard();

// Safari does not support requestIdleCallback / cancelIdleCallback.
// Polyfill with setTimeout so all call sites work without guards.
if (typeof window !== 'undefined' && !('requestIdleCallback' in window)) {
  (window as any).requestIdleCallback = (cb: IdleRequestCallback, opts?: IdleRequestOptions) => {
    const start = Date.now();
    return window.setTimeout(() => {
      cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      } as IdleDeadline);
    }, 1);
  };
  (window as any).cancelIdleCallback = (id: number) => window.clearTimeout(id);
}

import { captureUtmParams, getStoredUtmParams, captureRefCode, getStoredRefCode } from './services/utm/utmService';
captureUtmParams();
captureRefCode();

import React from 'react';
import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

if (import.meta.env.VITE_ENABLE_SENTRY === 'true' && import.meta.env.VITE_SENTRY_DSN && import.meta.env.MODE !== 'development') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Replay disabled: the DOM snapshot buffer was a significant source of
      // sustained memory pressure (up to 200MB on long sessions).
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
    ignoreErrors: [
      // Mixpanel's IndexedDB wrapper throws this when its IDB schema changes
      // between library versions and the user has a stale database. It's
      // transient, self-resolving, and entirely inside third-party code.
      /Failed to execute 'transaction' on 'IDBDatabase': One of the specified object stores was not found/,
      // Google Translate and other browser translation extensions mutate the DOM
      // by wrapping text nodes in <font>/<span> elements. React's VDOM then
      // disagrees with the real DOM and removeChild fails on the wrong parent.
      // This is entirely a third-party extension issue — nothing actionable on our end.
      /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/,
      /NotFoundError: The object can not be found here/,
    ],
  });
}

import './index.css';

// Schema pre-loading moved to lazy load - only when DocsApp is actually used
// This prevents loading heavy editor dependencies on initial page load
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { PostHogProvider } from '@posthog/react';
import posthog from 'posthog-js';
import { useAuth } from './hooks/useAuth';

import App from './App';
import reportWebVitals from './reportWebVitals';
import { createFileLogger } from './utils/logger';
import { ConversionHandler } from './pages/marketing/convert/shared/ConversionHandler';
import { userTrackingService } from './services/users/userTrackingService';
import { triggerGoogleAdsConversion } from './utils/analyticsHelpers';
import { API_BASE_URL } from './api/config';
import useAnalytics from './hooks/useAnalytics';
import { initializeEnvironmentFavicon } from './hooks/useDocumentTitle';
import electronTrackingService from './services/users/electronTrackingService';
import { IS_DEV } from './utils/devMode';
import { tokenRefresher } from './services/tokenRefresher';


// Initialize environment-specific favicon
initializeEnvironmentFavicon();

const log = createFileLogger('INDEX');

// Push the auth token to the Electron main process cache whenever it changes.
// This lets the main process read the token synchronously without calling
// executeJavaScript back into the renderer (which interrupts the renderer thread).
if (typeof window !== 'undefined' && (window as any).riverOS?.auth?.pushToken) {
  const pushTokenToMain = () => {
    const token = localStorage.getItem('authToken');
    (window as any).riverOS.auth.pushToken(token);
  };
  // Push on every token refresh event
  window.addEventListener('auth-refresh', pushTokenToMain);
  // Push once on startup (token may already be in localStorage from a previous session)
  pushTokenToMain();
}

// Note: toastService is now handled via React components in App.tsx

/**
 * TokenHandler Component
 * 
 * ARCHITECTURE OVERVIEW:
 * =====================
 * This app supports TWO authentication methods:
 * 
 * 1. **Auth0 OAuth** (Primary - for new users)
 *    - Industry-standard OAuth provider
 *    - Used for: Google sign-in, email/password via Auth0 Universal Login
 *    - Tokens stored by Auth0 SDK in: @@auth0spajs@@::... (localStorage)
 *    - Token also extracted and stored as 'authToken' for API calls
 * 
 * 2. **Custom JWT** (Legacy - for existing users)
 *    - Direct email/password login via LoginGUI.tsx
 *    - Backend issues JWT tokens
 *    - Tokens stored as: 'authToken', 'id_token'
 *    - Still supported for backwards compatibility
 * 
 * TOKEN STORAGE:
 * ==============
 * - Auth0 SDK: @@auth0spajs@@::CLIENT_ID::AUDIENCE::SCOPE (localStorage, all environments)
 * - Our API token: 'authToken' (used in 174+ places for API calls)
 * - ID token: 'id_token' (contains user email, used by useAuth)
 * 
 * CACHE LOCATION:
 * ===============
 * cacheLocation='localstorage' everywhere (localhost and production).
 * 
 * With useRefreshTokens=true and offline_access scope, Auth0 uses Refresh Token
 * Rotation — each use of a refresh token issues a new one and invalidates the old.
 * This makes localstorage safe enough: stolen tokens are single-use and rotate
 * immediately. The prior 'memory' strategy caused sign-outs every few hours because
 * the refresh token was lost on any page reload or browser tab suspension, leaving
 * getAccessTokenSilently() unable to renew an expired access token.
 * 
 * RESPONSIBILITIES:
 * =================
 * 1. Extract Auth0 access token and store as 'authToken' for API calls
 * 2. Complete backend authentication (/api/auth/complete)
 * 3. Handle funnel/persona context for onboarding flows
 * 4. Track user authentication in analytics
 * 5. Skip auth completion for routes that don't need it
 * 
 * See also:
 * - config/authRoutes.ts - Routes that skip auth completion
 * - hooks/useAuth.ts - Unified auth hook (Auth0 + custom JWT)
 * - pages/BlogAuthCallback.tsx - Subdomain auth callback handler
 */
const TokenHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, auth0IsAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [hasCompletedAuth, setHasCompletedAuth] = React.useState(false);
  const { trackSignup } = useAnalytics();

  // Track Electron app launch on component mount
  React.useEffect(() => {
    electronTrackingService.trackAppStartup();
  }, []);

  // Register the Auth0 silent-refresh function so non-hook code (fetchService, etc.) can use it.
  // useLayoutEffect fires synchronously after DOM mutations, before the browser paints, so the
  // refresher is available before any child component mounts and calls tryRefresh().
  React.useLayoutEffect(() => {
    if (getAccessTokenSilently) {
      tokenRefresher.register(getAccessTokenSilently, import.meta.env.VITE_AUTH0_AUDIENCE || '');
    }
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    const storeTokenAndCompleteAuth = async () => {
      if (IS_DEV) {
        log.log('[TokenHandler] Effect triggered:', { 
          isAuthenticated, 
          hasUser: !!user, 
          hasCompletedAuth,
          userSub: user?.sub 
        });
      }

      if (!isAuthenticated || !user) {
        if (IS_DEV) log.log('[TokenHandler] Not authenticated or no user, skipping');
        return;
      }

      // Custom-JWT users (email/password via useAuthForm) are handled entirely by
      // useAuthForm.handleLoginRedirect which already calls /auth/complete. Skip here
      // to prevent a duplicate concurrent call that can create duplicate orgs.
      if (!auth0IsAuthenticated) {
        if (IS_DEV) log.log('[TokenHandler] Custom-JWT session — /auth/complete handled by useAuthForm, skipping');
        setHasCompletedAuth(true);
        return;
      }

      // Check if we've already completed auth for this user
      if (hasCompletedAuth) {
        if (IS_DEV) log.log('[TokenHandler] Auth already completed, skipping');
        return;
      }

      // Check if there's already a custom auth token in localStorage
      const existingToken = localStorage.getItem('authToken');
      if (existingToken) {
        if (IS_DEV) log.log('[TokenHandler] Auth token already exists, checking if auth completion needed');
        
        // Check if we're on a route that should skip auth flow entirely
        // Uses centralized configuration from authRoutes.ts
        try {
          const { shouldSkipAuthCompletion } = await import('./config/authRoutes');
          const currentPath = window.location.pathname;
          
          if (shouldSkipAuthCompletion(currentPath)) {
            if (IS_DEV) {
              log.log('[TokenHandler] On skip-auth route with existing token - skipping auth completion entirely');
              log.log('[TokenHandler] Current path:', currentPath);
            }
            setHasCompletedAuth(true);
            return;
          }
        } catch (error) {
          log.error('[TokenHandler] Failed to load authRoutes config, continuing with auth completion:', error);
          // Fallback: Don't skip auth (safer to complete auth than skip)
          // Continue with auth completion flow below
        }
        
        // But still complete auth if we haven't yet (for non-app routes)
        if (!hasCompletedAuth) {
          if (IS_DEV) log.log('[TokenHandler] Will proceed with auth completion using existing token');
          // Continue to auth completion below
        } else {
          return;
        }
      }

      let token = existingToken;
      
      try {
        if (!existingToken) {
          if (IS_DEV) {
            log.log('[TokenHandler] Attempting to get Auth0 access token silently...');
            log.log('[TokenHandler] Auth0 config:', {
              domain: import.meta.env.VITE_AUTH0_DOMAIN,
              clientId: import.meta.env.VITE_AUTH0_CLIENT_ID?.substring(0, 10) + '...',
              audience: import.meta.env.VITE_AUTH0_AUDIENCE
            });
          }
          
          token = (await getAccessTokenSilently?.({
            authorizationParams: {
              audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
              scope: 'openid profile email offline_access'
            }
          })) || null;
          
          if (IS_DEV) {
            log.log('[TokenHandler] Successfully got Auth0 access token');
            log.log('[TokenHandler] Token length:', token?.length);
          }
          if (token) {
            localStorage.setItem('authToken', token);
            window.dispatchEvent(new Event('auth-refresh'));
          }
        }
        
        
        // Track user authentication in analytics
        userTrackingService.setUser(user.sub || '');
        
        // ✅ Only set if non-empty (filter out empty strings)
        const hasValidEmail = user.email && user.email.trim() !== '';
        const hasValidName = user.name || user.nickname;
        
        if (hasValidEmail || hasValidName) {
          userTrackingService.updateUserInfo({
            email: hasValidEmail ? user.email : undefined,
            name: hasValidName ? (user.name || user.nickname) : undefined,
            userId: user.sub || undefined
          });
        }
        
        const { default: mixpanelService } = await import('./services/analytics/mixpanelService');
        mixpanelService.ensureRecordingActive();

        // Complete authentication with backend (create user/space if needed)
        if (!hasCompletedAuth && token) {
          if (IS_DEV) {
            log.log('[TokenHandler] Completing Auth0 authentication with backend...');
            log.log('[TokenHandler] API endpoint:', `${API_BASE_URL}/auth/complete`);
            log.log('[TokenHandler] Token preview:', token.substring(0, 20) + '...');
          }
          
          // Check for pending funnel/persona info from signup modal
          const pendingFunnelId = localStorage.getItem('pendingFunnelId');
          const pendingPersona = localStorage.getItem('pendingPersona');
          const pendingPageGroup = localStorage.getItem('pendingPageGroup');
          const pendingPageSlug = localStorage.getItem('pendingPageSlug');
          const pendingTemplateId = localStorage.getItem('pendingTemplateId');
          
          // ✅ CLEAN: Import and use centralized tool funnel configuration
          const { isToolFunnel } = await import('./config/toolFunnels');
          
          // Check if user came from landing page OR tool funnel - if so, tell backend not to create space
          const landingContext = sessionStorage.getItem('landing_hero_context');
          const isToolFunnelValue = isToolFunnel(pendingFunnelId);
          const skipSpaceCreation = 
            (!!landingContext && pendingFunnelId === 'river_docs_landing_v1') || 
            isToolFunnelValue ||
            !!pendingTemplateId;
          
          if (IS_DEV && skipSpaceCreation) {
            if (isToolFunnelValue) {
              console.log('🛠️  [TokenHandler] Tool funnel detected - requesting backend to skip space creation');
              console.log('🛠️  [TokenHandler] Tool will create workspace with specific settings');
              console.log('🛠️  [TokenHandler] Funnel ID:', pendingFunnelId);
            } else {
              console.log('🏗️ [TokenHandler] Landing page context detected - requesting backend to skip space creation');
              console.log('🏗️ [TokenHandler] Landing page will create workspace after OAuth completes');
            }
          }
          
          if (IS_DEV) {
            log.log('[TokenHandler] Sending funnel context to backend:', {
              funnel_id: pendingFunnelId,
              persona: pendingPersona,
              page_group: pendingPageGroup,
              page_slug: pendingPageSlug,
              skip_space_creation: skipSpaceCreation
            });
          }
          
          try {
            // Read pre-signup onboarding data from localStorage
            let onboardingData;
            try {
              const stored = localStorage.getItem('pre_signup_onboarding_answers');

              if (stored) {
                const parsed = JSON.parse(stored);

                onboardingData = {
                  role: parsed.role || undefined,
                  interest: parsed.interest || parsed.interests || undefined
                };
                log.log('[TokenHandler] Including onboarding data from pre-signup:', onboardingData);
              }
            } catch (err) {
              log.error('[TokenHandler] Failed to parse pre-signup onboarding data:', err);
            }

            const utmParams = getStoredUtmParams();
            if (IS_DEV && utmParams) {
              log.log('[UTM] Attaching to OAuth complete:', utmParams);
            }
            
            const response = await fetch(`${API_BASE_URL}/auth/complete`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                funnel_id: pendingFunnelId || undefined,
                persona: pendingPersona || undefined,
                page_group: pendingPageGroup || undefined,
                page_slug: pendingPageSlug || undefined,
                skip_space_creation: skipSpaceCreation,
                onboarding_data: onboardingData,
                utm_source: utmParams?.utm_source,
                utm_medium: utmParams?.utm_medium,
                utm_campaign: utmParams?.utm_campaign,
                utm_content: utmParams?.utm_content,
                utm_term: utmParams?.utm_term,
                first_landing_page: utmParams?.first_landing_page,
                referrer: utmParams?.referrer,
                ref_code: getStoredRefCode() || undefined,
              })
            });

            if (IS_DEV) {
              log.log('[TokenHandler] Response status:', response.status);
              log.log('[TokenHandler] Response ok:', response.ok);
            }

            if (response.ok) {
              const result = await response.json();
              
              if (IS_DEV) log.log('[TokenHandler] Authentication completed:', result);

              const { useOnboardingStore } = await import('./stores/onboardingStore');
              const onboardingStore = useOnboardingStore.getState();
              onboardingStore.setIsNewUser(result.is_new_user || false);
              onboardingStore.setShouldOpenChat(result.is_new_user || false);

              // Clean up pre-signup onboarding data from localStorage after successful signup
              try {
                localStorage.removeItem('pre_signup_onboarding_answers');
                log.log('[TokenHandler] Cleaned up pre-signup onboarding data');
              } catch (err) {
                log.error('[TokenHandler] Failed to clean up pre-signup data:', err);
              }
              
              // ✅ FIX: Sync dataspace context from backend response
              // This ensures frontend context matches backend immediately after auth
              if (result.dataspace_context) {
                try {
                  const { useDataspaceContextStore } = await import('./stores/dataspaceContextStore');
                  const { default: useSystemStore } = await import('./stores/systemStore');
                  
                  const normalizedContext = {
                    type: result.dataspace_context.type,
                    orgId: result.dataspace_context.org_id || null,
                    orgName: result.dataspace_context.org_name || null
                  };
                  
                  if (IS_DEV) {
                    log.log('[TokenHandler] 🔄 Syncing dataspace context from backend:', normalizedContext);
                  }
                  
                  // Update both stores
                  useDataspaceContextStore.setState({ currentContext: normalizedContext });
                  useSystemStore.setState({ currentContext: normalizedContext });
                  
                  if (IS_DEV) {
                    log.log('[TokenHandler] ✅ Dataspace context synced');
                  }
                } catch (contextError) {
                  log.error('[TokenHandler] Failed to sync dataspace context:', contextError);
                }
              }
              
              setHasCompletedAuth(true);
              
              // Track Electron app login after successful authentication
              electronTrackingService.trackAppLogin();
              
              // Track successful signup for new OAuth users (if space_id indicates new user)
              if (result.space_id && user) {
                if (IS_DEV) log.log('[TokenHandler] OAuth user detected - tracking signup conversion');
                
                // Track in analytics with OAuth method
                await trackSignup('google_oauth', user.sub || 'unknown', {
                  email: user.email,
                  name: user.name || user.nickname,
                  auth_method: 'google_oauth',
                  space_id: result.space_id
                });
                
                // Trigger Google Ads conversion tracking
                triggerGoogleAdsConversion();
              }
              
              const authCurrentPath = window.location.pathname;

              const { shouldSkipPostAuthRedirect } = await import('./config/authRoutes');
              if (shouldSkipPostAuthRedirect(authCurrentPath)) {
                if (IS_DEV) {
                  log.log('[TokenHandler] On destination route — auth completed, skipping redirect:', authCurrentPath);
                }
                return;
              }
              
              // Check for pending funnel/redirect info from signup modal
              const pendingFunnelId = localStorage.getItem('pendingFunnelId');
              const pendingRedirectTo = sessionStorage.getItem('pendingRedirectTo') || localStorage.getItem('pendingRedirectTo');
              const pendingPersona = localStorage.getItem('pendingPersona');
              
              if (IS_DEV) {
                log.log('[TokenHandler] Checking for pending funnel info:', {
                  pendingFunnelId,
                  pendingRedirectTo,
                  pendingPersona
                });
              }
              
              // Check if there's a custom redirect specified (takes priority)
              if (pendingRedirectTo) {
                if (IS_DEV) log.log('[TokenHandler] ✅ Using custom redirect:', pendingRedirectTo);
                localStorage.removeItem('pendingFunnelId');
                localStorage.removeItem('pendingPersona');
                localStorage.removeItem('pendingRedirectTo');
                sessionStorage.removeItem('pendingRedirectTo');
                if (pendingRedirectTo.startsWith('http://') || pendingRedirectTo.startsWith('https://')) {
                  window.location.href = pendingRedirectTo;
                } else {
                  navigate(pendingRedirectTo, { replace: true });
                }
                return;
              }
              
              // ✅ CLEAN: For tool funnels, DON'T redirect - stay on current page
              // The tool's useEffect will detect auth and process pending data
              const { isToolFunnel: checkIsToolFunnel } = await import('./config/toolFunnels');
              if (checkIsToolFunnel(pendingFunnelId)) {
                if (IS_DEV) {
                  console.log('🛠️  [TokenHandler] Tool funnel - staying on current page');
                  console.log('🛠️  [TokenHandler] Tool component will process pending data');
                  console.log('🛠️  [TokenHandler] Funnel ID:', pendingFunnelId);
                }
                // Don't clear pending context or navigate - tool needs to know user just authenticated
                return;
              }
              
              // Check if this funnel uses multi-step onboarding (only non-tool funnels)
              const { TEMPLATE_ONBOARDING_FUNNELS: templateFunnelMap } = await import('./config/toolFunnels');
              const onboardingPersona = pendingFunnelId && templateFunnelMap[pendingFunnelId];
              
              // Handle special funnel redirects - only for multi-step onboarding funnels
              if (onboardingPersona) {
                if (IS_DEV) log.log('[TokenHandler] ✅ Redirecting to multi-step onboarding:', onboardingPersona);
                localStorage.removeItem('pendingFunnelId');
                localStorage.removeItem('pendingPersona');
                localStorage.removeItem('pendingRedirectTo');
                sessionStorage.removeItem('pendingRedirectTo');
                navigate(`/onboarding/${onboardingPersona}`, { replace: true });
                return;
              }
              
              // Suppress book project promo modal for users who signed up from the book landing page
              if (pendingFunnelId === 'book_formatter_v1') {
                const useUserStore = (await import('./stores/userStore')).default;
                useUserStore.getState().setOnboardingFlag('bookProjectPromoModalShown', true);
              }

              // If user came from a template CTA, create the template space and navigate there.
              // Backend already skipped generic space creation (skip_space_creation=true above).
              const tokenForTemplate = token;
              const templateIdToCreate = localStorage.getItem('pendingTemplateId') || sessionStorage.getItem('pendingTemplateId');
              if (templateIdToCreate && tokenForTemplate) {
                // Remove pending keys before the async call so a retry doesn't double-create.
                localStorage.removeItem('pendingTemplateId');
                sessionStorage.removeItem('pendingTemplateId');
                localStorage.removeItem('pendingFunnelId');
                localStorage.removeItem('pendingPersona');
                sessionStorage.removeItem('pendingRedirectTo');
                localStorage.removeItem('pendingPageGroup');
                localStorage.removeItem('pendingPageSlug');
                if (IS_DEV) console.log('🎯 [TokenHandler] Creating space from template:', templateIdToCreate);
                try {
                  const tRes = await fetch(`${API_BASE_URL}/spaces/from-template`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenForTemplate}` },
                    body: JSON.stringify({ templateId: templateIdToCreate })
                  });
                  if (tRes.ok) {
                    const tData = await tRes.json();
                    const templateSpaceId = tData.spaceId || tData._id || tData.id;
                    if (templateSpaceId) {
                      navigate(`/spaces/${templateSpaceId}`, { replace: true });
                      return;
                    }
                  } else {
                    log.error('[TokenHandler] from-template failed:', tRes.status);
                  }
                } catch (tErr) {
                  log.error('[TokenHandler] from-template error:', tErr);
                }
                // Template creation failed — restore the key so the user can retry
                // and surface an error rather than silently falling through.
                sessionStorage.setItem('pendingTemplateId', templateIdToCreate);
                import('./services/sessionManager').then(({ sessionManager }) => {
                  sessionManager.handleSessionExpired({
                    message: 'Could not create your workspace from that template. Please try again.'
                  });
                });
                return;
              }

              // Clear pending values
              localStorage.removeItem('pendingFunnelId');
              localStorage.removeItem('pendingPersona');
              localStorage.removeItem('pendingRedirectTo');
              sessionStorage.removeItem('pendingRedirectTo');
              localStorage.removeItem('pendingPageGroup');
              localStorage.removeItem('pendingPageSlug');
              
              // Redirect to user's space with welcome document
              const currentPath = window.location.pathname;
              const redirectUrl = result.redirect_to;
              
              if (IS_DEV) {
                log.log('[TokenHandler] Current path:', currentPath);
                log.log('[TokenHandler] Redirect URL from backend:', redirectUrl);
              }
              
              // Check if we're on a special flow that shouldn't redirect to space
              const isOnRiverConfig = currentPath === '/river-config';
              if (isOnRiverConfig || !result.space_id) {
                if (IS_DEV) {
                  console.log('🏗️ [TokenHandler] ✅ Special signup flow detected - skipping space redirect');
                  console.log('🏗️ [TokenHandler] User will stay on:', currentPath);
                }
                // Don't navigate - stay on current flow
                return;
              }
              
              // Check if there's stored context from landing page (shouldn't happen if skip_space_creation worked)
              const storedContext = sessionStorage.getItem('landing_hero_context');
              if (storedContext && result.space_id) {
                if (IS_DEV) {
                  console.log('⚠️ [TokenHandler] Backend created space even though landing context exists!');
                  console.log('⚠️ [TokenHandler] This means skip_space_creation flag was not respected');
                  console.log('⚠️ [TokenHandler] Letting landing page create workspace anyway (Space', result.space_id, 'will be orphaned)');
                }
                // Don't navigate - let landing page handle it
                return;
              }
              
              if (redirectUrl) {
                // ✅ NEW: For new users, keep chat open (skip QuickLauncher redirect)
                if (result.is_new_user) {
                  // Remove openQuickLauncher param from URL (handles all edge cases)
                  const finalRedirectUrl = redirectUrl
                    .replace(/[?]openQuickLauncher=true&/, '?')   // ?param& -> ?
                    .replace(/&openQuickLauncher=true/g, '')       // &param -> (remove)
                    .replace(/[?]openQuickLauncher=true$/, '');    // ?param (alone) -> (remove)
                  
                  if (IS_DEV) {
                    log.log('[TokenHandler] ✅ New user detected - redirecting with chat open:', finalRedirectUrl);
                    log.log('[TokenHandler] Skipped QuickLauncher to show welcome message');
                  }
                  navigate(finalRedirectUrl, { replace: true });
                } else {
                  if (IS_DEV) log.log('[TokenHandler] ✅ Navigating to user workspace:', redirectUrl);
                  navigate(redirectUrl, { replace: true });
                }
              } else {
                if (IS_DEV) log.error('[TokenHandler] ❌ No redirect_to provided by backend!');
                // Fallback: navigate to space if we have space_id
                if (result.space_id) {
                  let fallbackUrl = `/spaces/${result.space_id}`;
                  
                  // Check for stored context in fallback too
                  if (storedContext) {
                    try {
                      const parsed = JSON.parse(storedContext);
                      if (parsed.inputText) {
                        fallbackUrl += `?contextKey=landing_hero_context&prompt=${encodeURIComponent(parsed.inputText)}`;
                      if (IS_DEV) console.log('🎯 [TokenHandler] Added stored context to fallback URL');
                    }
                  } catch {}
                }
                  
                  if (IS_DEV) log.log('[TokenHandler] Using fallback redirect:', fallbackUrl);
                  navigate(fallbackUrl, { replace: true });
                }
              }
            } else {
              log.error('[TokenHandler] Auth completion failed:', response.status);
              if (IS_DEV) {
                try {
                  const errorText = await response.text();
                  log.error('[TokenHandler] Error response body:', errorText);
                } catch {
                  log.error('[TokenHandler] Could not read error response');
                }
              }
              // Mark as attempted so we don't retry endlessly, then surface the error.
              setHasCompletedAuth(true);
              import('./services/sessionManager').then(({ sessionManager }) => {
                sessionManager.handleSessionExpired({
                  message: 'Sign-in could not be completed. Please refresh the page or try again.'
                });
              });
            }
          } catch (authError) {
            if (IS_DEV) {
              log.error('[TokenHandler] Error completing authentication:', authError);
              if (authError instanceof Error) {
                log.error('[TokenHandler] Error message:', authError.message);
                log.error('[TokenHandler] Error stack:', authError.stack);
              }
            }
          }
        }
      } catch (error) {
        if (IS_DEV) {
          log.error('[TokenHandler] Error getting Auth0 token:', error);
          if (error instanceof Error) {
            log.error('[TokenHandler] Token error message:', error.message);
            log.error('[TokenHandler] Token error stack:', error.stack);
          }
        }
        
        // Enhanced error handling - detect expired/corrupted auth state
        const errorMessage = error instanceof Error ? error.message : '';
        const isMissingRefreshToken = errorMessage.includes('Missing Refresh Token');
        const isLoginRequired = errorMessage.includes('Login required');
        
        if (isMissingRefreshToken || isLoginRequired) {
          console.warn('🚨 [TokenHandler] Token error detected:', errorMessage);
          
          // Trigger session expired modal instead of hard logout
          import('./services/sessionManager').then(({ sessionManager }) => {
            sessionManager.handleSessionExpired({
              message: isMissingRefreshToken 
                ? 'Your session has expired. Please log in again to continue.'
                : 'Authentication error. Please log in again.'
            });
          });
          
          // Clear tokens but don't redirect immediately - let modal handle it
          import('./utils/logoutUtils').then(({ clearAllAuthState }) => {
            clearAllAuthState();
          });
        } else {
          // Other errors - just clear token
          if (!existingToken) {
            localStorage.removeItem('authToken');
          }
        }
      }
    };

    storeTokenAndCompleteAuth();
  }, [isAuthenticated, auth0IsAuthenticated, user, getAccessTokenSilently, hasCompletedAuth, navigate, logout]);

  return <>{children}</>;
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// Support deep-linking in Electron file:// by reading target route from the search params
const initialSearch = typeof window !== 'undefined' ? window.location.search : '';
const urlParams = new URLSearchParams(initialSearch);
const forcedRoute = urlParams.get('route');

if (forcedRoute && typeof window !== 'undefined' && typeof window.history !== 'undefined') {
  try {
    // Use relative path so asset base stays within build folder
    const cleanPath = forcedRoute.startsWith('/') ? forcedRoute.substring(1) : forcedRoute;
    window.history.replaceState(null, '', cleanPath);
  } catch {}
}

// Helper function to get canonical redirect URI
const getCanonicalRedirectUri = () => {
  const host = window.location.hostname;
  if (host.includes('bedrock.computer') || host.includes('riversystem.ai') || host.includes('rivereditor.com')) {
    // Always redirect to the root domain for consistency with Auth0 callback URLs
    return `https://${host}`;
  }
  // For local development, use the current origin
  return window.location.origin;
};

root.render(
  <BrowserRouter
    future={{
      v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN || ''}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID || ''}
        authorizationParams={{
          redirect_uri: getCanonicalRedirectUri(),
          audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
          scope: 'openid profile email offline_access',
        }}
        useRefreshTokens={true}
        cacheLocation="localstorage"
        cookieDomain={
          window.location.hostname.includes('bedrock.computer')
            ? '.bedrock.computer'
            : window.location.hostname.includes('riversystem.ai')
            ? '.riversystem.ai'
            : window.location.hostname.includes('rivereditor.com')
            ? '.rivereditor.com'
            : undefined
        }
        skipRedirectCallback={
          // CRITICAL: Skip OAuth callback processing on /blog-auth-callback route
          // This route relays the OAuth code to blog subdomains WITHOUT consuming it
          // If we don't skip, the code gets consumed here and blog subdomain auth fails
          window.location.pathname === '/blog-auth-callback'
        }
        onRedirectCallback={(appState) => {
          // For OAuth callback, don't immediately redirect to root
          // Let TokenHandler complete the authentication flow and determine the proper redirect
          const targetPath = appState?.returnTo;
          
          if (targetPath) {
            // If there's a specific returnTo path, use it
            console.log('🔄 [Auth0] onRedirectCallback - redirecting to specified path:', targetPath);
            window.history.replaceState(
              {},
              document.title,
              targetPath
            );
          } else {
            // If no returnTo, stay on current page and let TokenHandler handle the redirect
            // This prevents dropping users on root before authentication completes
            console.log('🔄 [Auth0] onRedirectCallback - no returnTo, letting TokenHandler handle redirect');
            // Don't navigate anywhere - TokenHandler will handle it after /auth/complete
          }
        }}
      >
        <PostHogProvider client={posthog}>
        <TokenHandler>
          <ConversionHandler />
          <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '12px', background: 'var(--bg-primary, #0f0f0f)', color: 'var(--text-primary, #e5e5e5)', fontFamily: 'var(--font-sans, system-ui, sans-serif)' }}>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: 500 }}>Something went wrong</p>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-tertiary, #6b7280)', maxWidth: '400px', textAlign: 'center' }}>{(error as Error)?.message}</p>
              <button onClick={resetError} style={{ marginTop: '8px', padding: '6px 16px', fontSize: '13px', cursor: 'pointer', borderRadius: '6px', border: '1px solid var(--border-primary, #2a2a2a)', background: 'var(--bg-secondary, #1a1a1a)', color: 'var(--text-primary, #e5e5e5)' }}>
                Try again
              </button>
            </div>
          )}>
            <App />
          </Sentry.ErrorBoundary>
        </TokenHandler>
        </PostHogProvider>
      </Auth0Provider>
    </BrowserRouter>,
);

reportWebVitals();
